import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Download, Mail, Phone, MapPin, Linkedin, Check } from 'lucide-react';

const templateStyles = [
  { value: 'classic', name: 'Classic', description: 'Traditional single-column, highly ATS-friendly', ats: 'excellent' },
  { value: 'professional', name: 'Professional', description: 'Bold headers, clean sections, ATS-safe', ats: 'excellent' },
  { value: 'modern', name: 'Modern', description: 'Contemporary with color accents, ATS-friendly', ats: 'excellent' },
  { value: 'simple', name: 'Simple', description: 'Minimalist, maximum ATS compatibility', ats: 'excellent' },
  { value: 'minimal', name: 'Minimal', description: 'Ultra-clean, text-focused, maximum ATS', ats: 'excellent' },
  { value: 'technical', name: 'Technical', description: 'Skills-first layout for IT/Engineering', ats: 'excellent' },
];

const CVPreviewModal = ({ cv, isOpen, onClose, onDownload }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(cv?.template_style || 'classic');
  
  if (!cv) return null;

  const pd = cv.personal_details || {};

  // Classic Template - Traditional single column
  const ClassicTemplate = () => (
    <div className="bg-white p-8 md:p-10" style={{ fontFamily: 'Georgia, serif' }}>
      {/* Header */}
      <div className="text-center border-b-2 border-slate-800 pb-4 mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 uppercase tracking-wide">
          {pd.full_name || 'Your Name'}
        </h1>
        <div className="flex flex-wrap justify-center gap-4 mt-3 text-sm text-slate-600">
          {pd.email && <span>{pd.email}</span>}
          {pd.phone && <span>| {pd.phone}</span>}
          {(pd.city || pd.province) && <span>| {[pd.city, pd.province].filter(Boolean).join(', ')}</span>}
          {pd.linkedin && <span>| {pd.linkedin}</span>}
        </div>
        {/* SA-Specific Info */}
        {(pd.date_of_birth || pd.gender || pd.nationality || pd.drivers_license || pd.availability || pd.health_status || pd.criminal_record || pd.willingness_to_travel || pd.dependants) && (
          <div className="flex flex-wrap justify-center gap-3 mt-2 text-xs text-slate-500">
            {pd.date_of_birth && <span>DOB: {new Date(pd.date_of_birth).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}</span>}
            {pd.gender && pd.gender !== 'Prefer not to say' && <span>| {pd.gender}</span>}
            {pd.nationality && <span>| {pd.nationality}</span>}
            {pd.drivers_license && pd.drivers_license !== 'none' && <span>| License: {pd.drivers_license}</span>}
            {pd.own_transport && <span>| Own Transport: Yes</span>}
            {pd.availability && <span>| Available: {pd.availability}</span>}
            {pd.health_status && pd.health_status !== 'Prefer not to say' && <span>| Health: {pd.health_status}</span>}
            {pd.criminal_record && pd.criminal_record !== 'Prefer not to say' && <span>| Criminal Record: {pd.criminal_record}</span>}
            {pd.willingness_to_travel && <span>| Travel: {pd.willingness_to_travel}</span>}
            {pd.dependants && <span>| Dependants: {pd.dependants}</span>}
          </div>
        )}
      </div>

      {/* Professional Summary */}
      {cv.professional_summary && (
        <section className="mb-6">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider border-b border-slate-300 pb-1 mb-3">
            Professional Summary
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed">{cv.professional_summary}</p>
        </section>
      )}

      {/* Work Experience */}
      {cv.work_experience && cv.work_experience.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider border-b border-slate-300 pb-1 mb-3">
            Work Experience
          </h2>
          {cv.work_experience.map((exp, index) => (
            <div key={exp.id || index} className="mb-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-slate-900">{exp.job_title}</h3>
                  <p className="text-slate-600 italic">{exp.company}</p>
                </div>
                <span className="text-sm text-slate-500">
                  {exp.start_date} - {exp.is_current ? 'Present' : exp.end_date}
                </span>
              </div>
              {exp.description && (
                <p className="text-sm text-slate-700 mt-2 leading-relaxed">{exp.description}</p>
              )}
            </div>
          ))}
        </section>
      )}

      {/* Education */}
      {cv.education && cv.education.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider border-b border-slate-300 pb-1 mb-3">
            Education
          </h2>
          {cv.education.map((edu, index) => (
            <div key={edu.id || index} className="mb-3">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-slate-900">{edu.qualification}</h3>
                  <p className="text-slate-600">{edu.institution}</p>
                  {edu.field_of_study && <p className="text-slate-500 text-sm">{edu.field_of_study}</p>}
                </div>
                <span className="text-sm text-slate-500">
                  {edu.start_year} - {edu.is_current ? 'Present' : edu.end_year}
                </span>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Skills */}
      {cv.skills && cv.skills.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider border-b border-slate-300 pb-1 mb-3">
            Skills
          </h2>
          <p className="text-sm text-slate-700">
            {cv.skills.map(s => `${s.name} (${s.level})`).join(' • ')}
          </p>
        </section>
      )}

      {/* Languages */}
      {cv.languages && cv.languages.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider border-b border-slate-300 pb-1 mb-3">
            Languages
          </h2>
          <p className="text-sm text-slate-700">
            {cv.languages.map(l => `${l.name} (${l.proficiency})`).join(' • ')}
          </p>
        </section>
      )}

      {/* Certifications */}
      {cv.certifications && cv.certifications.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider border-b border-slate-300 pb-1 mb-3">
            Certifications
          </h2>
          {cv.certifications.map((cert, index) => (
            <p key={cert.id || index} className="text-sm text-slate-700">
              {cert.name} - {cert.issuer} {cert.year && `(${cert.year})`}
            </p>
          ))}
        </section>
      )}

      {/* Professional Memberships */}
      {cv.professional_memberships && cv.professional_memberships.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider border-b border-slate-300 pb-1 mb-3">
            Professional Memberships
          </h2>
          {cv.professional_memberships.map((membership, index) => (
            <div key={membership.id || index} className="text-sm text-slate-700 mb-1">
              <span className="font-medium">{membership.body_name}</span>
              {membership.membership_type && <span> - {membership.membership_type}</span>}
              {membership.membership_number && <span className="text-slate-500"> (#{membership.membership_number})</span>}
              {membership.year_joined && <span className="text-slate-500"> · Since {membership.year_joined}</span>}
            </div>
          ))}
        </section>
      )}

      {/* References */}
      {cv.references && cv.references.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider border-b border-slate-300 pb-1 mb-3">
            References
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {cv.references.map((ref, index) => (
              <div key={ref.id || index} className="text-sm">
                <p className="font-bold text-slate-900">{ref.name}</p>
                <p className="text-slate-600">{ref.relationship}{ref.company && ` at ${ref.company}`}</p>
                {ref.phone && <p className="text-slate-500">{ref.phone}</p>}
                {ref.email && <p className="text-slate-500">{ref.email}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Hobbies & Interests */}
      {cv.hobbies && cv.hobbies.length > 0 && (
        <section>
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider border-b border-slate-300 pb-1 mb-3">
            Hobbies & Interests
          </h2>
          <p className="text-sm text-slate-700">
            {cv.hobbies.filter(h => h && h.trim()).join(' • ')}
          </p>
        </section>
      )}
    </div>
  );

  // Professional Template - Bold headers, clean sections
  const ProfessionalTemplate = () => (
    <div className="bg-white" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div className="bg-slate-900 text-white px-8 py-6">
        <h1 className="text-2xl md:text-3xl font-bold">{pd.full_name || 'Your Name'}</h1>
        <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-300">
          {pd.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {pd.email}</span>}
          {pd.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {pd.phone}</span>}
          {(pd.city || pd.province) && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {[pd.city, pd.province].filter(Boolean).join(', ')}</span>}
        </div>
        {/* SA-Specific Info */}
        {(pd.date_of_birth || pd.gender || pd.nationality || pd.drivers_license || pd.availability || pd.health_status || pd.criminal_record || pd.willingness_to_travel || pd.dependants) && (
          <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-400">
            {pd.date_of_birth && <span>DOB: {new Date(pd.date_of_birth).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}</span>}
            {pd.gender && pd.gender !== 'Prefer not to say' && <span>• {pd.gender}</span>}
            {pd.nationality && <span>• {pd.nationality}</span>}
            {pd.drivers_license && pd.drivers_license !== 'none' && <span>• License: {pd.drivers_license}</span>}
            {pd.own_transport && <span>• Own Transport</span>}
            {pd.availability && <span>• Available: {pd.availability}</span>}
            {pd.health_status && pd.health_status !== 'Prefer not to say' && <span>• Health: {pd.health_status}</span>}
            {pd.criminal_record && pd.criminal_record !== 'Prefer not to say' && <span>• Criminal Record: {pd.criminal_record}</span>}
            {pd.willingness_to_travel && <span>• Travel: {pd.willingness_to_travel}</span>}
            {pd.dependants && <span>• Dependants: {pd.dependants}</span>}
          </div>
        )}
      </div>

      <div className="px-8 py-6">
        {/* Professional Summary */}
        {cv.professional_summary && (
          <section className="mb-6">
            <h2 className="text-lg font-bold text-slate-900 bg-slate-100 px-3 py-2 -mx-3 mb-3">
              PROFESSIONAL SUMMARY
            </h2>
            <p className="text-sm text-slate-700 leading-relaxed">{cv.professional_summary}</p>
          </section>
        )}

        {/* Work Experience */}
        {cv.work_experience && cv.work_experience.length > 0 && (
          <section className="mb-6">
            <h2 className="text-lg font-bold text-slate-900 bg-slate-100 px-3 py-2 -mx-3 mb-3">
              WORK EXPERIENCE
            </h2>
            {cv.work_experience.map((exp, index) => (
              <div key={exp.id || index} className="mb-4 pl-3 border-l-4 border-slate-900">
                <div className="flex flex-wrap justify-between items-start gap-2">
                  <div>
                    <h3 className="font-bold text-slate-900">{exp.job_title}</h3>
                    <p className="text-slate-600">{exp.company}</p>
                  </div>
                  <span className="text-sm text-slate-500 bg-slate-100 px-2 py-1 rounded">
                    {exp.start_date} - {exp.is_current ? 'Present' : exp.end_date}
                  </span>
                </div>
                {exp.description && (
                  <p className="text-sm text-slate-700 mt-2">{exp.description}</p>
                )}
              </div>
            ))}
          </section>
        )}

        {/* Education */}
        {cv.education && cv.education.length > 0 && (
          <section className="mb-6">
            <h2 className="text-lg font-bold text-slate-900 bg-slate-100 px-3 py-2 -mx-3 mb-3">
              EDUCATION
            </h2>
            {cv.education.map((edu, index) => (
              <div key={edu.id || index} className="mb-3 pl-3 border-l-4 border-slate-400">
                <div className="flex flex-wrap justify-between items-start gap-2">
                  <div>
                    <h3 className="font-bold text-slate-900">{edu.qualification}</h3>
                    <p className="text-slate-600">{edu.institution}</p>
                  </div>
                  <span className="text-sm text-slate-500">
                    {edu.start_year} - {edu.is_current ? 'Present' : edu.end_year}
                  </span>
                </div>
              </div>
            ))}
          </section>
        )}

        {/* Skills */}
        {cv.skills && cv.skills.length > 0 && (
          <section className="mb-6">
            <h2 className="text-lg font-bold text-slate-900 bg-slate-100 px-3 py-2 -mx-3 mb-3">
              SKILLS
            </h2>
            <div className="flex flex-wrap gap-2">
              {cv.skills.map((skill, index) => (
                <span key={skill.id || index} className="px-3 py-1 bg-slate-200 text-slate-700 text-sm rounded">
                  {skill.name}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Languages */}
        {cv.languages && cv.languages.length > 0 && (
          <section className="mb-6">
            <h2 className="text-lg font-bold text-slate-900 bg-slate-100 px-3 py-2 -mx-3 mb-3">
              LANGUAGES
            </h2>
            <p className="text-sm text-slate-700">
              {cv.languages.map(l => `${l.name} (${l.proficiency})`).join(' • ')}
            </p>
          </section>
        )}

        {/* Certifications */}
        {cv.certifications && cv.certifications.length > 0 && (
          <section className="mb-6">
            <h2 className="text-lg font-bold text-slate-900 bg-slate-100 px-3 py-2 -mx-3 mb-3">
              CERTIFICATIONS
            </h2>
            {cv.certifications.map((cert, index) => (
              <p key={cert.id || index} className="text-sm text-slate-700 mb-1">
                {cert.name} - {cert.issuer} {cert.year && `(${cert.year})`}
              </p>
            ))}
          </section>
        )}

        {/* Professional Memberships */}
        {cv.professional_memberships && cv.professional_memberships.length > 0 && (
          <section className="mb-6">
            <h2 className="text-lg font-bold text-slate-900 bg-slate-100 px-3 py-2 -mx-3 mb-3">
              PROFESSIONAL MEMBERSHIPS
            </h2>
            {cv.professional_memberships.map((membership, index) => (
              <p key={membership.id || index} className="text-sm text-slate-700 mb-1">
                <span className="font-medium">{membership.body_name}</span>
                {membership.membership_type && ` - ${membership.membership_type}`}
                {membership.membership_number && <span className="text-slate-500"> (#{membership.membership_number})</span>}
                {membership.year_joined && <span className="text-slate-500"> · Since {membership.year_joined}</span>}
              </p>
            ))}
          </section>
        )}

        {/* References */}
        {cv.references && cv.references.length > 0 && (
          <section>
            <h2 className="text-lg font-bold text-slate-900 bg-slate-100 px-3 py-2 -mx-3 mb-3">
              REFERENCES
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {cv.references.map((ref, index) => (
                <div key={ref.id || index} className="text-sm p-3 bg-slate-50 rounded">
                  <p className="font-bold text-slate-900">{ref.name}</p>
                  <p className="text-slate-600">{ref.relationship}</p>
                  {ref.phone && <p className="text-slate-500">{ref.phone}</p>}
                  {ref.email && <p className="text-slate-500">{ref.email}</p>}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );

  // Modern Template - Contemporary single-column with color accents (ATS-friendly)
  const ModernTemplate = () => (
    <div className="bg-white p-8" style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
      {/* Header with accent color */}
      <div className="border-l-4 border-sky-500 pl-4 mb-6">
        <h1 className="text-2xl font-bold text-sky-600 uppercase tracking-wide">{pd.full_name || 'Your Name'}</h1>
        <p className="text-sm text-slate-600 mt-1">
          {[pd.email, pd.phone, [pd.city, pd.province].filter(Boolean).join(', '), pd.linkedin].filter(Boolean).join('  |  ')}
        </p>
        {/* SA-Specific Info */}
        {(pd.date_of_birth || pd.gender || pd.nationality || pd.drivers_license || pd.availability) && (
          <p className="text-xs text-slate-500 mt-1">
            {[
              pd.date_of_birth && `DOB: ${new Date(pd.date_of_birth).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}`,
              pd.gender && pd.gender !== 'Prefer not to say' && pd.gender,
              pd.nationality,
              pd.drivers_license && pd.drivers_license !== 'none' && `License: ${pd.drivers_license}`,
              pd.own_transport && 'Own Transport',
              pd.availability && `Available: ${pd.availability}`,
              pd.health_status && pd.health_status !== 'Prefer not to say' && `Health: ${pd.health_status}`,
              pd.criminal_record && pd.criminal_record !== 'Prefer not to say' && `Criminal Record: ${pd.criminal_record}`,
              pd.willingness_to_travel && `Travel: ${pd.willingness_to_travel}`,
              pd.dependants && `Dependants: ${pd.dependants}`
            ].filter(Boolean).join('  |  ')}
          </p>
        )}
      </div>

      {/* Professional Summary */}
      {cv.professional_summary && (
        <section className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-0.5 bg-sky-500"></div>
            <h2 className="text-sm font-bold text-sky-600 uppercase tracking-wider">Profile</h2>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">{cv.professional_summary}</p>
        </section>
      )}

      {/* Work Experience */}
      {cv.work_experience && cv.work_experience.length > 0 && (
        <section className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-0.5 bg-sky-500"></div>
            <h2 className="text-sm font-bold text-sky-600 uppercase tracking-wider">Experience</h2>
          </div>
          {cv.work_experience.map((exp, index) => (
            <div key={exp.id || index} className="mb-4">
              <h3 className="font-bold text-slate-900 uppercase text-sm">{exp.job_title}</h3>
              <p className="text-sm text-sky-700">{exp.company} | {exp.start_date} - {exp.is_current ? 'Present' : exp.end_date}</p>
              {exp.description && (
                <p className="text-sm text-slate-600 mt-1">{exp.description}</p>
              )}
            </div>
          ))}
        </section>
      )}

      {/* Education */}
      {cv.education && cv.education.length > 0 && (
        <section className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-0.5 bg-sky-500"></div>
            <h2 className="text-sm font-bold text-sky-600 uppercase tracking-wider">Education</h2>
          </div>
          {cv.education.map((edu, index) => (
            <div key={edu.id || index} className="mb-2">
              <h3 className="font-bold text-slate-900 uppercase text-sm">{edu.qualification}</h3>
              <p className="text-sm text-slate-600">{edu.institution} | {edu.field_of_study} | {edu.end_year}</p>
            </div>
          ))}
        </section>
      )}

      {/* Skills */}
      {cv.skills && cv.skills.length > 0 && (
        <section className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-0.5 bg-sky-500"></div>
            <h2 className="text-sm font-bold text-sky-600 uppercase tracking-wider">Skills</h2>
          </div>
          <p className="text-sm text-slate-700">{cv.skills.map(s => s.name).join('  •  ')}</p>
        </section>
      )}

      {/* Languages */}
      {cv.languages && cv.languages.length > 0 && (
        <section className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-0.5 bg-sky-500"></div>
            <h2 className="text-sm font-bold text-sky-600 uppercase tracking-wider">Languages</h2>
          </div>
          <p className="text-sm text-slate-700">{cv.languages.map(l => `${l.name} (${l.proficiency})`).join('  •  ')}</p>
        </section>
      )}

      {/* Certifications */}
      {cv.certifications && cv.certifications.length > 0 && (
        <section className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-0.5 bg-sky-500"></div>
            <h2 className="text-sm font-bold text-sky-600 uppercase tracking-wider">Certifications</h2>
          </div>
          {cv.certifications.map((cert, index) => (
            <p key={cert.id || index} className="text-sm text-slate-700">
              {cert.name} - {cert.issuer} ({cert.year})
            </p>
          ))}
        </section>
      )}

      {/* Professional Memberships */}
      {cv.professional_memberships && cv.professional_memberships.length > 0 && (
        <section className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-0.5 bg-sky-500"></div>
            <h2 className="text-sm font-bold text-sky-600 uppercase tracking-wider">Professional Memberships</h2>
          </div>
          {cv.professional_memberships.map((membership, index) => (
            <p key={membership.id || index} className="text-sm text-slate-700">
              <span className="font-semibold">{membership.body_name}</span>
              {membership.membership_type && ` | ${membership.membership_type}`}
              {membership.membership_number && ` | #${membership.membership_number}`}
              {membership.year_joined && ` | Since ${membership.year_joined}`}
            </p>
          ))}
        </section>
      )}

      {/* References */}
      {cv.references && cv.references.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-0.5 bg-sky-500"></div>
            <h2 className="text-sm font-bold text-sky-600 uppercase tracking-wider">References</h2>
          </div>
          {cv.references.map((ref, index) => (
            <p key={ref.id || index} className="text-sm text-slate-700">
              <span className="font-semibold">{ref.name}</span> | {ref.relationship} at {ref.company} | {ref.email} | {ref.phone}
            </p>
          ))}
        </section>
      )}
    </div>
  );

  // Simple Template - Minimalist, maximum ATS compatibility
  const SimpleTemplate = () => (
    <div className="bg-white p-8" style={{ fontFamily: 'Times New Roman, serif' }}>
      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-black">{pd.full_name || 'Your Name'}</h1>
        <p className="text-sm text-black mt-1">
          {[pd.email, pd.phone, [pd.city, pd.province].filter(Boolean).join(', ')].filter(Boolean).join(' | ')}
        </p>
        {pd.linkedin && <p className="text-sm text-black">{pd.linkedin}</p>}
        {/* SA-Specific Info */}
        {(pd.date_of_birth || pd.gender || pd.nationality || pd.drivers_license || pd.availability || pd.health_status || pd.criminal_record || pd.willingness_to_travel || pd.dependants) && (
          <p className="text-xs text-gray-600 mt-1">
            {[
              pd.date_of_birth && `DOB: ${new Date(pd.date_of_birth).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}`,
              pd.gender && pd.gender !== 'Prefer not to say' && pd.gender,
              pd.nationality,
              pd.drivers_license && pd.drivers_license !== 'none' && `License: ${pd.drivers_license}`,
              pd.own_transport && 'Own Transport',
              pd.availability && `Available: ${pd.availability}`,
              pd.health_status && pd.health_status !== 'Prefer not to say' && `Health: ${pd.health_status}`,
              pd.criminal_record && pd.criminal_record !== 'Prefer not to say' && `Criminal Record: ${pd.criminal_record}`,
              pd.willingness_to_travel && `Travel: ${pd.willingness_to_travel}`,
              pd.dependants && `Dependants: ${pd.dependants}`
            ].filter(Boolean).join(' | ')}
          </p>
        )}
      </div>

      {/* Professional Summary */}
      {cv.professional_summary && (
        <section className="mb-5">
          <h2 className="text-base font-bold text-black border-b border-black pb-1 mb-2">
            PROFESSIONAL SUMMARY
          </h2>
          <p className="text-sm text-black">{cv.professional_summary}</p>
        </section>
      )}

      {/* Work Experience */}
      {cv.work_experience && cv.work_experience.length > 0 && (
        <section className="mb-5">
          <h2 className="text-base font-bold text-black border-b border-black pb-1 mb-2">
            WORK EXPERIENCE
          </h2>
          {cv.work_experience.map((exp, index) => (
            <div key={exp.id || index} className="mb-3">
              <p className="font-bold text-black">{exp.job_title}</p>
              <p className="text-sm text-black">{exp.company} | {exp.start_date} - {exp.is_current ? 'Present' : exp.end_date}</p>
              {exp.description && (
                <p className="text-sm text-black mt-1">{exp.description}</p>
              )}
            </div>
          ))}
        </section>
      )}

      {/* Education */}
      {cv.education && cv.education.length > 0 && (
        <section className="mb-5">
          <h2 className="text-base font-bold text-black border-b border-black pb-1 mb-2">
            EDUCATION
          </h2>
          {cv.education.map((edu, index) => (
            <div key={edu.id || index} className="mb-2">
              <p className="font-bold text-black">{edu.qualification}</p>
              <p className="text-sm text-black">{edu.institution} | {edu.start_year} - {edu.is_current ? 'Present' : edu.end_year}</p>
            </div>
          ))}
        </section>
      )}

      {/* Skills */}
      {cv.skills && cv.skills.length > 0 && (
        <section className="mb-5">
          <h2 className="text-base font-bold text-black border-b border-black pb-1 mb-2">
            SKILLS
          </h2>
          <p className="text-sm text-black">{cv.skills.map(s => s.name).join(', ')}</p>
        </section>
      )}

      {/* Languages */}
      {cv.languages && cv.languages.length > 0 && (
        <section className="mb-5">
          <h2 className="text-base font-bold text-black border-b border-black pb-1 mb-2">
            LANGUAGES
          </h2>
          <p className="text-sm text-black">{cv.languages.map(l => `${l.name} (${l.proficiency})`).join(', ')}</p>
        </section>
      )}

      {/* Certifications */}
      {cv.certifications && cv.certifications.length > 0 && (
        <section className="mb-5">
          <h2 className="text-base font-bold text-black border-b border-black pb-1 mb-2">
            CERTIFICATIONS
          </h2>
          {cv.certifications.map((cert, index) => (
            <p key={cert.id || index} className="text-sm text-black">
              {cert.name} - {cert.issuer} {cert.year && `(${cert.year})`}
            </p>
          ))}
        </section>
      )}

      {/* Professional Memberships */}
      {cv.professional_memberships && cv.professional_memberships.length > 0 && (
        <section className="mb-5">
          <h2 className="text-base font-bold text-black border-b border-black pb-1 mb-2">
            PROFESSIONAL MEMBERSHIPS
          </h2>
          {cv.professional_memberships.map((membership, index) => (
            <p key={membership.id || index} className="text-sm text-black">
              {membership.body_name}
              {membership.membership_type && ` - ${membership.membership_type}`}
              {membership.membership_number && ` (#${membership.membership_number})`}
              {membership.year_joined && ` - Since ${membership.year_joined}`}
            </p>
          ))}
        </section>
      )}

      {/* References */}
      {cv.references && cv.references.length > 0 && (
        <section>
          <h2 className="text-base font-bold text-black border-b border-black pb-1 mb-2">
            REFERENCES
          </h2>
          {cv.references.map((ref, index) => (
            <div key={ref.id || index} className="mb-2">
              <p className="text-sm text-black">
                <span className="font-bold">{ref.name}</span> - {ref.relationship}
                {ref.company && ` at ${ref.company}`}
                {ref.phone && ` | ${ref.phone}`}
                {ref.email && ` | ${ref.email}`}
              </p>
            </div>
          ))}
        </section>
      )}
    </div>
  );

  // Minimal Template - Ultra-clean, text-focused, maximum ATS
  const MinimalTemplate = () => (
    <div className="bg-white p-8" style={{ fontFamily: 'Georgia, serif' }}>
      {/* Header - Centered and minimal */}
      <div className="text-center mb-8">
        <h1 className="text-xl font-normal text-zinc-900">{pd.full_name || 'Your Name'}</h1>
        <p className="text-sm text-zinc-600 mt-2">
          {[pd.email, pd.phone, [pd.city, pd.province].filter(Boolean).join(', '), pd.linkedin].filter(Boolean).join(' | ')}
        </p>
        {/* SA-Specific Info */}
        {(pd.date_of_birth || pd.gender || pd.nationality || pd.drivers_license || pd.availability) && (
          <p className="text-xs text-zinc-500 mt-1">
            {[
              pd.date_of_birth && `DOB: ${new Date(pd.date_of_birth).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}`,
              pd.gender && pd.gender !== 'Prefer not to say' && pd.gender,
              pd.nationality,
              pd.drivers_license && pd.drivers_license !== 'none' && `License: ${pd.drivers_license}`,
              pd.own_transport && 'Own Transport',
              pd.availability && `Available: ${pd.availability}`,
              pd.health_status && pd.health_status !== 'Prefer not to say' && `Health: ${pd.health_status}`,
              pd.criminal_record && pd.criminal_record !== 'Prefer not to say' && `Criminal Record: ${pd.criminal_record}`,
              pd.willingness_to_travel && `Travel: ${pd.willingness_to_travel}`,
              pd.dependants && `Dependants: ${pd.dependants}`
            ].filter(Boolean).join(' | ')}
          </p>
        )}
      </div>

      {/* Summary */}
      {cv.professional_summary && (
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-zinc-800 mb-2">Summary</h2>
          <p className="text-sm text-zinc-700 leading-relaxed">{cv.professional_summary}</p>
        </section>
      )}

      {/* Experience */}
      {cv.work_experience && cv.work_experience.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-zinc-800 mb-2">Experience</h2>
          {cv.work_experience.map((exp, index) => (
            <div key={exp.id || index} className="mb-3">
              <p className="text-sm text-zinc-900">
                <span className="font-semibold">{exp.job_title}</span> at {exp.company} ({exp.start_date} - {exp.is_current ? 'Present' : exp.end_date})
              </p>
              {exp.description && <p className="text-sm text-zinc-600 mt-1">{exp.description}</p>}
            </div>
          ))}
        </section>
      )}

      {/* Education */}
      {cv.education && cv.education.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-zinc-800 mb-2">Education</h2>
          {cv.education.map((edu, index) => (
            <p key={edu.id || index} className="text-sm text-zinc-700 mb-1">
              <span className="font-semibold">{edu.qualification}</span> - {edu.institution} ({edu.end_year})
            </p>
          ))}
        </section>
      )}

      {/* Skills */}
      {cv.skills && cv.skills.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-zinc-800 mb-2">Skills</h2>
          <p className="text-sm text-zinc-700">{cv.skills.map(s => s.name).join(', ')}</p>
        </section>
      )}

      {/* Languages */}
      {cv.languages && cv.languages.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-zinc-800 mb-2">Languages</h2>
          <p className="text-sm text-zinc-700">{cv.languages.map(l => `${l.name} (${l.proficiency})`).join(', ')}</p>
        </section>
      )}

      {/* Professional Memberships */}
      {cv.professional_memberships && cv.professional_memberships.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-zinc-800 mb-2">Professional Memberships</h2>
          {cv.professional_memberships.map((membership, index) => (
            <p key={membership.id || index} className="text-sm text-zinc-700">
              {membership.body_name}
              {membership.membership_type && ` - ${membership.membership_type}`}
              {membership.membership_number && ` (#${membership.membership_number})`}
              {membership.year_joined && ` - Since ${membership.year_joined}`}
            </p>
          ))}
        </section>
      )}

      {/* References */}
      {cv.references && cv.references.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-zinc-800 mb-2">References</h2>
          {cv.references.map((ref, index) => (
            <p key={ref.id || index} className="text-sm text-zinc-700">
              {ref.name} - {ref.relationship} at {ref.company} | {ref.email} | {ref.phone}
            </p>
          ))}
        </section>
      )}
    </div>
  );

  // Technical Template - Skills-first for IT/Engineering
  const TechnicalTemplate = () => (
    <div className="bg-white p-8" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div className="border-b-2 border-red-600 pb-4 mb-6">
        <h1 className="text-2xl font-bold text-zinc-900">{pd.full_name || 'Your Name'}</h1>
        <p className="text-sm text-zinc-600 mt-1">
          {[pd.email, pd.phone, [pd.city, pd.province].filter(Boolean).join(', '), pd.linkedin].filter(Boolean).join(' | ')}
        </p>
        {/* SA-Specific Info */}
        {(pd.date_of_birth || pd.gender || pd.nationality || pd.drivers_license || pd.availability || pd.health_status || pd.criminal_record || pd.willingness_to_travel || pd.dependants) && (
          <p className="text-xs text-zinc-500 mt-1">
            {[
              pd.date_of_birth && `DOB: ${new Date(pd.date_of_birth).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}`,
              pd.gender && pd.gender !== 'Prefer not to say' && pd.gender,
              pd.nationality,
              pd.drivers_license && pd.drivers_license !== 'none' && `License: ${pd.drivers_license}`,
              pd.own_transport && 'Own Transport',
              pd.availability && `Available: ${pd.availability}`,
              pd.health_status && pd.health_status !== 'Prefer not to say' && `Health: ${pd.health_status}`,
              pd.criminal_record && pd.criminal_record !== 'Prefer not to say' && `Criminal Record: ${pd.criminal_record}`,
              pd.willingness_to_travel && `Travel: ${pd.willingness_to_travel}`,
              pd.dependants && `Dependants: ${pd.dependants}`
            ].filter(Boolean).join(' | ')}
          </p>
        )}
      </div>

      {/* SKILLS FIRST - Technical CVs prioritize skills */}
      {cv.skills && cv.skills.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-bold text-red-600 uppercase tracking-wider mb-3">Technical Skills</h2>
          <div className="flex flex-wrap gap-2">
            {cv.skills.map((skill, index) => (
              <span key={skill.id || index} className="px-2 py-1 bg-red-50 text-red-800 text-xs rounded border border-red-200">
                {skill.name} ({skill.level})
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Summary */}
      {cv.professional_summary && (
        <section className="mb-6">
          <h2 className="text-sm font-bold text-red-600 uppercase tracking-wider mb-3">Professional Summary</h2>
          <p className="text-sm text-zinc-700 leading-relaxed">{cv.professional_summary}</p>
        </section>
      )}

      {/* Experience */}
      {cv.work_experience && cv.work_experience.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-bold text-red-600 uppercase tracking-wider mb-3">Professional Experience</h2>
          {cv.work_experience.map((exp, index) => (
            <div key={exp.id || index} className="mb-4 pl-3 border-l-2 border-red-200">
              <h3 className="font-bold text-zinc-900">{exp.job_title}</h3>
              <p className="text-sm text-zinc-600">{exp.company} | {exp.start_date} - {exp.is_current ? 'Present' : exp.end_date}</p>
              {exp.description && <p className="text-sm text-zinc-700 mt-2">{exp.description}</p>}
            </div>
          ))}
        </section>
      )}

      {/* Education */}
      {cv.education && cv.education.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-bold text-red-600 uppercase tracking-wider mb-3">Education</h2>
          {cv.education.map((edu, index) => (
            <div key={edu.id || index} className="mb-2">
              <p className="font-bold text-zinc-900">{edu.qualification}</p>
              <p className="text-sm text-zinc-600">{edu.institution} | {edu.field_of_study} | {edu.end_year}</p>
            </div>
          ))}
        </section>
      )}

      {/* Certifications - Important for tech */}
      {cv.certifications && cv.certifications.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-bold text-red-600 uppercase tracking-wider mb-3">Certifications</h2>
          {cv.certifications.map((cert, index) => (
            <p key={cert.id || index} className="text-sm text-zinc-700">
              <span className="font-semibold">{cert.name}</span> - {cert.issuer} ({cert.year})
            </p>
          ))}
        </section>
      )}

      {/* Languages */}
      {cv.languages && cv.languages.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-bold text-red-600 uppercase tracking-wider mb-3">Languages</h2>
          <p className="text-sm text-zinc-700">{cv.languages.map(l => `${l.name} (${l.proficiency})`).join(', ')}</p>
        </section>
      )}

      {/* Professional Memberships */}
      {cv.professional_memberships && cv.professional_memberships.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-bold text-red-600 uppercase tracking-wider mb-3">Professional Memberships</h2>
          {cv.professional_memberships.map((membership, index) => (
            <p key={membership.id || index} className="text-sm text-zinc-700">
              <span className="font-semibold">{membership.body_name}</span>
              {membership.membership_type && ` | ${membership.membership_type}`}
              {membership.membership_number && ` | #${membership.membership_number}`}
              {membership.year_joined && ` | Since ${membership.year_joined}`}
            </p>
          ))}
        </section>
      )}

      {/* References */}
      {cv.references && cv.references.length > 0 && (
        <section>
          <h2 className="text-sm font-bold text-red-600 uppercase tracking-wider mb-3">References</h2>
          {cv.references.map((ref, index) => (
            <p key={ref.id || index} className="text-sm text-zinc-700">
              <span className="font-semibold">{ref.name}</span> | {ref.relationship} at {ref.company} | {ref.email} | {ref.phone}
            </p>
          ))}
        </section>
      )}
    </div>
  );

  const renderTemplate = () => {
    switch (selectedTemplate) {
      case 'professional':
        return <ProfessionalTemplate />;
      case 'modern':
        return <ModernTemplate />;
      case 'simple':
        return <SimpleTemplate />;
      case 'minimal':
        return <MinimalTemplate />;
      case 'technical':
        return <TechnicalTemplate />;
      default:
        return <ClassicTemplate />;
    }
  };

  const currentStyle = templateStyles.find(s => s.value === selectedTemplate) || templateStyles[0];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden p-0 bg-slate-100">
        <DialogHeader className="px-4 md:px-6 py-4 border-b border-slate-200 bg-white sticky top-0 z-10">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg font-semibold text-slate-900">CV Preview - Choose Template</DialogTitle>
              <Button
                onClick={() => onDownload(selectedTemplate)}
                className="bg-slate-900 hover:bg-slate-800 text-white shadow-lg"
                size="sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </div>
            
            {/* Template Selector */}
            <div className="flex flex-wrap gap-2">
              {templateStyles.map((style) => (
                <button
                  key={style.value}
                  onClick={() => setSelectedTemplate(style.value)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border-2 ${
                    selectedTemplate === style.value 
                      ? 'bg-slate-900 text-white border-slate-900' 
                      : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                  }`}
                  data-testid={`preview-template-${style.value}`}
                >
                  {selectedTemplate === style.value && <Check className="w-4 h-4" />}
                  <span>{style.name}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    style.ats === 'excellent' ? 'bg-emerald-100 text-emerald-700' :
                    style.ats === 'good' ? 'bg-blue-100 text-blue-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {style.ats === 'excellent' ? 'ATS ★★★' : style.ats === 'good' ? 'ATS ★★' : 'ATS ★'}
                  </span>
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500">
              Selected: <span className="font-medium text-slate-700">{currentStyle.name}</span> - {currentStyle.description}
            </p>
          </div>
        </DialogHeader>
        
        <div className="overflow-y-auto max-h-[calc(95vh-140px)] p-4 md:p-6 bg-slate-200">
          {/* CV Document */}
          <div 
            className="bg-white shadow-xl mx-auto overflow-hidden"
            style={{ 
              width: '100%', 
              maxWidth: '800px',
              minHeight: '1000px',
            }}
          >
            {renderTemplate()}
          </div>
          
          {/* ATS Info */}
          <div className="text-center mt-4 text-sm bg-white rounded-lg p-4 max-w-xl mx-auto">
            {currentStyle.ats === 'excellent' ? (
              <div className="text-emerald-700">
                <p className="font-medium">★★★ Excellent ATS Compatibility</p>
                <p className="text-xs mt-1 text-slate-500">Single-column layout ensures ATS systems read your CV correctly. Recommended for online job applications.</p>
              </div>
            ) : currentStyle.ats === 'good' ? (
              <div className="text-blue-700">
                <p className="font-medium">★★ Good ATS Compatibility</p>
                <p className="text-xs mt-1 text-slate-500">Clean formatting works well with most Applicant Tracking Systems.</p>
              </div>
            ) : (
              <div className="text-amber-700 bg-amber-50 rounded-lg p-3 border border-amber-200">
                <p className="font-semibold">⚠️ Limited ATS Compatibility</p>
                <p className="text-xs mt-2 text-amber-800 leading-relaxed">
                  <strong>Why?</strong> ATS systems read CVs left-to-right, top-to-bottom. 
                  Two-column layouts cause the system to mix your sidebar content (skills, contact info) 
                  with your main content (experience, education), resulting in scrambled or rejected applications.
                </p>
                <p className="text-xs mt-2 text-amber-800">
                  <strong>Best for:</strong> Direct email applications, networking, or when you know a human will read it first.
                </p>
                <p className="text-xs mt-2 text-amber-800">
                  <strong>Avoid for:</strong> Careers24, Indeed, PNet, LinkedIn Easy Apply, or any online job portal.
                </p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CVPreviewModal;
